import React, { useEffect, useMemo, useState } from "react";
import "./ReturnDetailModal.css";

const defaultReasons = [
  "Damage Items",
  "Wrong Item",
  "Quality Issue",
  "Missing Item",
];

const getProductItems = (ordersDetail = {}) => {
  const productData = ordersDetail?.product_data || {};
  const itemGroups = [
    productData?.confirmed_items,
    productData?.pending_items,
    productData?.substitute_items,
    ordersDetail?.confirmed_items,
    ordersDetail?.pending_items,
    ordersDetail?.substitute_items,
  ];

  return itemGroups.flatMap((group) => (Array.isArray(group) ? group : []));
};

const getItemId = (item, index) =>
  String(
    item?.return_request_item_id ||
      item?.return_item_id ||
      item?.booking_order_detail_id ||
      item?.booking_order_item_id ||
      item?.order_detail_id ||
      item?.cart_detail_id ||
      item?.cart_item_id ||
      item?.id ||
      item?.product_variant_id ||
      item?.variant_id ||
      item?.product_id ||
      index
  );

const getItemTitle = (item) =>
  item?.title ||
  item?.name ||
  item?.product_name ||
  item?.product_title ||
  item?.product?.name ||
  item?.product?.title ||
  "Product";

const getItemImage = (item) =>
  item?.image ||
  item?.product_image ||
  item?.product_cover_image ||
  item?.image_url ||
  item?.thumbnail ||
  item?.product?.image ||
  item?.product?.product_cover_image ||
  "assets/images/thumbs/product-two-img1.png";

const getItemUnit = (item) =>
  item?.weight_unit ||
  item?.portion ||
  item?.unit ||
  item?.size ||
  item?.product?.weight_unit ||
  item?.product?.portion ||
  "";

const getItemPrice = (item, currency) => {
  const price =
    item?.product_price ||
    item?.price ||
    item?.amount ||
    item?.total_amount ||
    item?.product?.price ||
    item?.product?.product_price ||
    "";

  if (!price) return "";

  const formattedPrice = String(price);
  return /[a-zA-Z₹$€£]/.test(formattedPrice) ? formattedPrice : `${currency} ${formattedPrice}`;
};

const getItemQuantity = (item) =>
  item?.quantity ??
  item?.qty ??
  item?.product_quantity ??
  item?.product_qty ??
  item?.order_quantity ??
  item?.item_quantity ??
  item?.cart_quantity ??
  item?.product?.quantity ??
  item?.product?.qty ??
  1;

const getItemPriceQuantity = (item, currency) => {
  const price = getItemPrice(item, currency);
  const quantity = getItemQuantity(item);

  return price ? `${price} * ${quantity}` : "";
};

const isItemReturnable = (item) => {
  const notReturnableValue =
    item?.not_returnable ??
    item?.notReturnable ??
    item?.is_not_returnable ??
    item?.isNotReturnable;

  if (notReturnableValue !== undefined && notReturnableValue !== null && notReturnableValue !== "") {
    if (notReturnableValue === true || notReturnableValue === 1 || notReturnableValue === "1") {
      return false;
    }

    const normalizedNotReturnable = String(notReturnableValue).toLowerCase();
    if (["true", "yes", "not_returnable", "not returnable", "blocked"].includes(normalizedNotReturnable)) {
      return false;
    }
  }

  const value =
    item?.is_returnable ??
    item?.returnable ??
    item?.return_status ??
    item?.is_return_allowed ??
    item?.can_return;

  if (value === undefined || value === null || value === "") return true;
  if (value === true || value === 1 || value === "1") return true;

  const normalized = String(value).toLowerCase();
  return ["true", "yes", "returnable", "allowed"].includes(normalized);
};

const ReturnDetailModal = ({
  show,
  onClose,
  onSubmit,
  ordersDetail,
  returnItems,
  loading = false,
  error = "",
  submitting = false,
}) => {
  const items = useMemo(
    () => (Array.isArray(returnItems) ? returnItems : getProductItems(ordersDetail)),
    [ordersDetail, returnItems]
  );
  const currency = ordersDetail?.currency || "INR";
  const [selectedReason, setSelectedReason] = useState(defaultReasons[0]);
  const [comment, setComment] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    if (show) {
      setSelectedReason(defaultReasons[0]);
      setComment("");
      setSelectedItems([]);
      return;
    }

    setUploadedImages((prevImages) => {
      prevImages.forEach((image) => URL.revokeObjectURL(image.preview));
      return [];
    });
  }, [show]);

  const displayImages = uploadedImages.length
    ? uploadedImages.map((image) => image.preview)
    : items.map((item) => getItemImage(item)).filter(Boolean).slice(0, 3);

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 3);
    const nextImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));

    setUploadedImages((prevImages) => {
      prevImages.forEach((image) => URL.revokeObjectURL(image.preview));
      return nextImages;
    });
    event.target.value = "";
  };

  const toggleItem = (itemId, returnable) => {
    if (!returnable) return;

    setSelectedItems((prevItems) =>
      prevItems.includes(itemId)
        ? prevItems.filter((id) => id !== itemId)
        : [...prevItems, itemId]
    );
  };

  const handleSubmit = () => {
    if (!selectedReason || !selectedItems.length || loading || submitting) return;

    onSubmit({
      reason: selectedReason,
      comment: comment.trim(),
      selectedItems,
      images: uploadedImages.map((image) => image.file),
    });
  };

  if (!show) return null;

  return (
    <div className="return-detail-backdrop">
      <div className="return-detail-modal">
        <div className="return-detail-header">
          <span>Return Details</span>
          <button type="button" className="return-detail-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="return-detail-body">
          <div className="return-detail-label-row">
            <span>Upload Products Images</span>
            <small>Multiple files can be added</small>
          </div>

          <div className="return-detail-images">
            {displayImages.map((image, index) => (
              <img key={`${image}-${index}`} src={image} alt={`Return ${index + 1}`} />
            ))}
            <label className="return-detail-upload">
              +
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={submitting}
              />
            </label>
          </div>

          <select
            className="return-detail-select"
            value={selectedReason}
            onChange={(event) => setSelectedReason(event.target.value)}
          >
            {defaultReasons.map((reason) => (
              <option key={reason} value={reason}>
                {reason}
              </option>
            ))}
          </select>

          <label className="return-detail-comment-label">Add comment if any</label>
          <textarea
            className="return-detail-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Damage items"
            rows={4}
          />

          <div className="return-detail-items">
            {loading && <div className="return-detail-state">Loading return items...</div>}
            {!loading && error && <div className="return-detail-state error">{error}</div>}
            {!loading && !error && !items.length && (
              <div className="return-detail-state">No return items found.</div>
            )}
            {!loading && !error && items.map((item, index) => {
              const itemId = getItemId(item, index);
              const returnable = isItemReturnable(item);
              const selected = selectedItems.includes(itemId);
              const image = getItemImage(item);
              const itemPrice = getItemPrice(item, currency);
              const priceQuantity = getItemPriceQuantity(item, currency);

              return (
                <button
                  type="button"
                  key={itemId}
                  className={`return-detail-item ${selected ? "selected" : ""}`}
                  onClick={() => toggleItem(itemId, returnable)}
                  disabled={!returnable}
                >
                  <span className="return-detail-checkbox">
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!returnable || submitting}
                      onChange={() => toggleItem(itemId, returnable)}
                      onClick={(event) => event.stopPropagation()}
                    />
                  </span>
                  <img src={image} alt={getItemTitle(item)} />
                  <div className="return-detail-item-info">
                    <strong>{getItemTitle(item)}</strong>
                    {priceQuantity && (
                      <small className="return-detail-price-qty">{priceQuantity}</small>
                    )}
                    <span>{getItemUnit(item)}</span>
                    <b>{itemPrice}</b>
                  </div>
                  {!returnable && <em>Not Returnable</em>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="return-detail-actions">
          <button type="button" className="return-detail-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="return-detail-submit"
            onClick={handleSubmit}
            disabled={!selectedReason || !selectedItems.length || loading || submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnDetailModal;
